import { useQuery } from '@tanstack/react-query';
import { apiFetch, streamSSE } from '@/lib/api/client';
import { getActiveTenantId } from '@/lib/tenant';
import type { ChatRequest, ChatSession, ChatUsage, SessionMessage } from '@/lib/api/types';

export function useSessions(
  agentId: string | undefined,
  opts?: { limit?: number; offset?: number },
) {
  const limit = opts?.limit ?? 50;
  const offset = opts?.offset ?? 0;
  return useQuery({
    queryKey: ['sessions', getActiveTenantId(), agentId, limit, offset],
    queryFn: () =>
      apiFetch<ChatSession[]>(
        `/sessions?agent_id=${encodeURIComponent(agentId ?? '')}&limit=${limit}&offset=${offset}`,
      ),
    enabled: Boolean(agentId),
  });
}

/** Transcript of an existing session (chat- or email-originated alike). */
export function useSessionMessages(sessionId: string | undefined) {
  return useQuery({
    queryKey: ['session-messages', getActiveTenantId(), sessionId],
    queryFn: () => apiFetch<SessionMessage[]>(`/sessions/${sessionId}/messages`),
    enabled: Boolean(sessionId),
  });
}

export interface ChatStreamHandlers {
  onToken: (chunk: string) => void;
  onUsage?: (usage: ChatUsage) => void;
  onSession?: (sessionId: string) => void;
  onError?: (message: string) => void;
  signal?: AbortSignal;
}

/**
 * Streams a chat completion from POST /chat (text/event-stream). Token chunks
 * arrive as "token"/"message" events; a final "usage" event carries token
 * accounting. Plain-text data and an OpenAI-style "[DONE]" sentinel are also
 * tolerated.
 */
export async function streamChat(req: ChatRequest, handlers: ChatStreamHandlers): Promise<void> {
  await streamSSE(
    '/chat',
    req,
    (msg) => {
      if (msg.data === '[DONE]') return;

      let parsed: unknown;
      try {
        parsed = JSON.parse(msg.data);
      } catch {
        // Non-JSON data: treat as a raw token.
        handlers.onToken(msg.data);
        return;
      }

      const obj = (parsed ?? {}) as Record<string, unknown>;
      const type = typeof obj.type === 'string' ? obj.type : msg.event;

      if (type === 'session') {
        if (typeof obj.session_id === 'string') handlers.onSession?.(obj.session_id);
        return;
      }
      if (type === 'usage') {
        handlers.onUsage?.(parsed as ChatUsage);
        return;
      }
      if (type === 'error') {
        handlers.onError?.(typeof obj.message === 'string' ? obj.message : 'Model error');
        return;
      }
      if (type === 'done') return;

      // token / message event
      const content =
        typeof obj.content === 'string'
          ? obj.content
          : typeof obj.delta === 'string'
            ? obj.delta
            : typeof obj.text === 'string'
              ? obj.text
              : '';
      if (content) handlers.onToken(content);
    },
    handlers.signal,
  );
}
